import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { installmentsApi, bookingsApi } from '../api/endpoints';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const INST_BADGE = {
  Paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Pending: 'bg-amber-50 text-amber-700 border-amber-200',
  Partial: 'bg-blue-50 text-blue-700 border-blue-200',
  Overdue: 'bg-rose-50 text-rose-700 border-rose-200',
};

const Payments = () => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [bookingsMap, setBookingsMap] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Tabs & Filters
  const [tab, setTab] = useState('overdue');
  const [q, setQ] = useState('');
  
  // Router Driven Filter States
  const [applyDashFilter, setApplyDashFilter] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [list, sum, bookings] = await Promise.all([
        installmentsApi.list(),
        installmentsApi.summary(),
        bookingsApi.list(),
      ]);
      setData(list); setSummary(sum);
      const m = {}; bookings.forEach(b => { m[b.id] = b; });
      setBookingsMap(m);
    } catch (e) { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Intercept routing payload
  useEffect(() => {
    if (location.state?.filterStatus) {
      setTab(location.state.filterStatus);
      setApplyDashFilter(true);
    } else {
      setTab('overdue');
      setApplyDashFilter(false);
    }
  }, [location.state]);

  const canEdit = hasPermission('payments', 'edit');

  const filtered = useMemo(() => {
    return data.filter(i => {
      const b = bookingsMap[i.booking_id] || {};
      const text = (b.customer_name || '') + ' ' + (b.unit_no || '') + ' ' + (b.project_name || '') + ' ' + i.milestone;
      
      // Search logic
      if (q && !text.toLowerCase().includes(q.toLowerCase())) return false;
      
      // Tab logic
      if (tab === 'overdue') return i.is_overdue && i.status !== 'Paid';
      if (tab === 'due-soon') {
        if (i.status === 'Paid' || i.is_overdue) return false;
        const today = new Date(); const due = new Date(i.due_date || '1900-01-01');
        const diff = (due - today) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff <= 30;
      }
      if (tab === 'pending') return i.status === 'Pending' || i.status === 'Partial';
      if (tab === 'paid') return i.status === 'Paid';
      return true;
    });
  }, [data, bookingsMap, q, tab]);

  const clearFilters = () => {
    setTab('all');
    setQ('');
    setApplyDashFilter(false);
    navigate('.', { replace: true, state: null });
  };

  const runReminders = async () => {
    try { const r = await installmentsApi.runReminders(); toast.success(`Auto-reminders fired: ${r.reminders_sent}`); fetchAll(); }
    catch { toast.error('Failed to trigger'); }
  };

  const sendReminder = async (iid) => {
    try { await installmentsApi.remind(iid); toast.success('Reminder sent'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const downloadLetter = async (iid) => {
    try {
      const resp = await api.get(`/installments/${iid}/demand-letter.pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch { toast.error('Failed to generate letter'); }
  };

  const downloadBulkLetters = async (scope) => {
    try {
      const resp = await api.get(`/payments/bulk-demand-letters.pdf`, {
        params: { scope, days: 30 },
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(resp.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      toast.success(`Bulk ${scope.replace('_', ' ')} demand letters generated`);
    } catch (e) {
      if (e?.response?.status === 404) toast.info(`No ${scope.replace('_', ' ')} installments found.`);
      else toast.error('Failed to generate bulk letters');
    }
  };

  const stats = summary ? [
    { label: 'Total Demand', value: inr(summary.total_demand), icon: 'Receipt', color: 'text-brand-navy' },
    { label: 'Collected', value: inr(summary.total_paid), icon: 'CheckCircle2', color: 'text-emerald-600' },
    { label: 'Overdue', value: `${summary.overdue_count} (${inr(summary.overdue_amount)})`, icon: 'AlertTriangle', color: 'text-rose-600' },
    { label: 'Pending', value: summary.pending_count, icon: 'Clock', color: 'text-amber-600' },
  ] : [];

  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-gradient-to-r from-[#0F1F38] via-[#1B3A5C] to-[#2A4F7A] text-white p-5 lg:p-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Icons.IndianRupee className="w-5 h-5 text-brand-gold" />
            <div className="text-xs uppercase tracking-[0.25em] text-brand-gold">Demand Letters & Collections</div>
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold">Payment Follow-ups</h2>
          <p className="text-slate-300 text-sm mt-1 max-w-xl">Track milestone-based demand letters, balance, and fire automated WhatsApp reminders.</p>
        </div>
        
        {applyDashFilter && (
          <Button variant="ghost" onClick={clearFilters} className="text-blue-200 hover:text-white hover:bg-white/10 uppercase tracking-wider text-[10px] font-bold">
            Clear Active Filter
          </Button>
        )}

        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => downloadBulkLetters('overdue')} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              <Icons.FilesIcon className="w-4 h-4 mr-1.5" />Bulk Overdue Letters
            </Button>
            <Button onClick={() => downloadBulkLetters('due_soon')} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              <Icons.CalendarClock className="w-4 h-4 mr-1.5" />Bulk Due-in-30d
            </Button>
            <Button onClick={runReminders} className="bg-brand-gold text-brand-navy hover:bg-amber-300">
              <Icons.Zap className="w-4 h-4 mr-1.5" />Run Reminders Now
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {stats.map(s => {
          const Icon = Icons[s.icon];
          return (
            <Card key={s.label} className="border-slate-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide">{s.label}</div>
                  <div className="font-display text-xl font-bold mt-1">{s.value}</div>
                </div>
                <Icon className={`w-7 h-7 ${s.color}`} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search customer / unit / milestone..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="overdue">Overdue</TabsTrigger>
              <TabsTrigger value="due-soon">Due in 30d</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Customer / Unit</th>
                <th className="px-4 py-3">Milestone</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right hidden md:table-cell">Paid</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}><td colSpan={7} className="p-2"><Skeleton className="h-10 w-full" /></td></tr>
              ))}
              {!loading && filtered.map(i => {
                const b = bookingsMap[i.booking_id] || {};
                const showStatus = i.is_overdue && i.status === 'Pending' ? 'Overdue' : i.status;
                return (
                  <tr key={i.id} className="hover:bg-slate-50" data-testid={`pay-row-${i.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800 truncate">{b.customer_name || i.booking_id}</div>
                      <div className="text-[11px] text-slate-500 truncate">Unit {b.unit_no || '—'} • {b.project_name || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{i.milestone}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{i.due_date}</td>
                    <td className="px-4 py-3 text-right font-medium">{inr(i.amount)}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 hidden md:table-cell">{inr(i.paid_amount)}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={INST_BADGE[showStatus] || 'bg-slate-50'}>{showStatus}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && i.status !== 'Paid' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => downloadLetter(i.id)} data-testid={`pay-letter-${i.id}`} title="Generate Demand Letter PDF">
                              <Icons.FileWarning className="w-3.5 h-3.5 mr-1 text-rose-600" />Letter
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => sendReminder(i.id)} data-testid={`pay-remind-${i.id}`}>
                              <Icons.MessageCircle className="w-3.5 h-3.5 mr-1" />Remind
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">No installments in this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Payments;